import { ChevronDownIcon, HelpCircleIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

/**
 * How the file this dialog reads comes out of Odoo, step by step, and how new customers get into
 * Odoo and back. Here rather than in a document nobody finds: this is where somebody stands with
 * the wrong file in hand. Closed until asked for — after the first time it is in the way.
 *
 * No filter in Odoo: every contact goes, and the import itself leaves out what is no customer
 * here — invoice and delivery addresses, people without a company — and says so in the preview.
 */
export function OdooExportGuide() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border">
      <CollapsibleTrigger asChild>
        <Button type="button" variant="ghost" className="w-full justify-start">
          <HelpCircleIcon />
          {t('customerTransfer.odooGuide.title')}
          <ChevronDownIcon className={`ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="grid gap-4 px-4 pb-4 text-sm">
        <p className="text-muted-foreground">{t('customerTransfer.odooGuide.intro')}</p>

        <ol className="grid list-decimal gap-2 pl-5">
          <li>{t('customerTransfer.odooGuide.steps.open')}</li>
          <li>{t('customerTransfer.odooGuide.steps.select')}</li>
          <li>{t('customerTransfer.odooGuide.steps.export')}</li>
          <li>{t('customerTransfer.odooGuide.steps.format')}</li>
          <li className="grid gap-2">
            <span>{t('customerTransfer.odooGuide.steps.fields')}</span>
            <ul className="bg-muted/50 grid gap-0.5 rounded-md border p-2 font-mono text-xs">
              {t('customerTransfer.odooGuide.fieldList', { returnObjects: true }).map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
            <span className="text-muted-foreground">{t('customerTransfer.odooGuide.fieldsHint')}</span>
          </li>
          <li>{t('customerTransfer.odooGuide.steps.template')}</li>
          <li>{t('customerTransfer.odooGuide.steps.import')}</li>
        </ol>

        <div className="grid gap-2">
          <p className="font-medium">{t('customerTransfer.odooGuide.roundTrip.title')}</p>
          <ol className="grid list-decimal gap-2 pl-5">
            <li>{t('customerTransfer.odooGuide.roundTrip.first')}</li>
            <li>{t('customerTransfer.odooGuide.roundTrip.download')}</li>
            <li>{t('customerTransfer.odooGuide.roundTrip.intoOdoo')}</li>
            <li>{t('customerTransfer.odooGuide.roundTrip.back')}</li>
          </ol>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
